import { isValidPhoneBR } from "@/lib/phoneUtils";

/**
 * entregasValidation.js — regras de campos obrigatórios compartilhadas por
 * formulário individual (EntregasForm) e lote (LoteRowsTable).
 *
 * Regra fev/2026 (definida pelo usuário):
 * 1. Obrigatórios: bairro_id, tipo_id, numero_pedido (ou receita+revenda, ou
 *    pedidos_multiplos), cliente_nome, cliente_telefone, forma_pgto_id
 * 2. Exceção: se tipo for "Coleta" ou "Transferência", forma_pgto_id
 *    NÃO é obrigatório
 * 3. Save fica desabilitado até que todos os obrigatórios estejam preenchidos
 * 4. Telefone: mínimo 10 dígitos (fixo) ou 11 dígitos (celular)
 */

/**
 * Verifica se o tipo escolhido é Coleta ou Transferência (case-insensitive,
 * ignora acentos). Aceita `tipos` como array de {id, nome}.
 */
export function isTipoSemFormaObrigatoria(tipo_id, tipos = []) {
  if (!tipo_id) return false;
  const nome = (tipos.find((t) => t.id === tipo_id)?.nome || "").toLowerCase();
  // Normaliza: remove acentos, comparações substring
  const norm = nome.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return norm.includes("coleta") || norm.includes("transferencia");
}

/**
 * Detecta se o tipo é "Receita+Revenda" — nesse caso, o pedido não é 1 número
 * único mas 2 (receita e revenda). Mantém compatibilidade com a lógica
 * existente em outros helpers.
 */
export function isTipoReceitaRevenda(tipo_id, tipos = []) {
  if (!tipo_id) return false;
  const nome = (tipos.find((t) => t.id === tipo_id)?.nome || "").toLowerCase();
  return nome.includes("receita") && nome.includes("revenda");
}

/**
 * Valida um objeto de entrega (form individual ou linha do lote).
 * Retorna Set<string> com os nomes dos campos que estão FALTANDO.
 *
 * Nomes de campo usados na UI (mesmos que aparecem nos data-testid):
 *   - "bairro_id", "tipo_id", "forma_pgto_id"
 *   - "cliente_nome", "cliente_telefone"
 *   - "numero_pedido" (single mode)
 *   - "numero_pedido_receita" + "numero_pedido_revenda" (receita+revenda)
 *   - "pedidos_multiplos" (quando qtd_pedidos > 1 e algum item vazio)
 */
export function getMissingRequiredFields(obj, tipos = []) {
  const missing = new Set();
  const clean = (v) => String(v ?? "").trim();

  if (!clean(obj.bairro_id)) missing.add("bairro_id");
  if (!clean(obj.tipo_id)) missing.add("tipo_id");
  if (!clean(obj.cliente_nome)) missing.add("cliente_nome");
  // Telefone: obrigatório E precisa ter formato válido (10 ou 11 dígitos).
  // Usamos a mesma key `cliente_telefone` pra ambos os casos — o painel de
  // erro (buildErrorMessage) explica se é vazio ou curto. Assim o ring
  // vermelho no input funciona sem precisar de key extra.
  if (!clean(obj.cliente_telefone) || !isValidPhoneBR(obj.cliente_telefone)) {
    missing.add("cliente_telefone");
  }

  // Forma de pagamento — só é obrigatória se tipo não for Coleta/Transferência
  if (!isTipoSemFormaObrigatoria(obj.tipo_id, tipos)) {
    if (!clean(obj.forma_pgto_id)) missing.add("forma_pgto_id");
  }

  // Número(s) do pedido — depende do tipo/qtd
  if (isTipoReceitaRevenda(obj.tipo_id, tipos)) {
    if (!clean(obj.numero_pedido_receita)) missing.add("numero_pedido_receita");
    if (!clean(obj.numero_pedido_revenda)) missing.add("numero_pedido_revenda");
  } else {
    const qtd = Math.max(1, parseInt(obj.qtd_pedidos, 10) || 1);
    if (qtd > 1) {
      const arr = Array.isArray(obj.pedidos_multiplos) ? obj.pedidos_multiplos : [];
      // Todos os N campos precisam estar preenchidos
      const preenchidos = arr.filter((p) => clean(p)).length;
      if (preenchidos < qtd) missing.add("pedidos_multiplos");
    } else if (!clean(obj.numero_pedido)) {
      missing.add("numero_pedido");
    }
  }

  return missing;
}

/**
 * Atalho: retorna true se todos os obrigatórios estão preenchidos.
 * Use pra habilitar/desabilitar o botão de salvar.
 */
export function hasAllRequiredFilled(obj, tipos = []) {
  return getMissingRequiredFields(obj, tipos).size === 0;
}

/**
 * Labels amigáveis pra cada campo (usadas em mensagens de erro).
 */
export const FIELD_LABELS = {
  bairro_id: "Bairro",
  tipo_id: "Tipo de Entrega",
  forma_pgto_id: "Forma de Pagamento",
  cliente_nome: "Cliente",
  cliente_telefone: "Telefone",
  numero_pedido: "Nº do Pedido",
  numero_pedido_receita: "Nº Pedido Receita",
  numero_pedido_revenda: "Nº Pedido Revenda",
  pedidos_multiplos: "Números dos Pedidos",
};

/**
 * Constrói mensagem de erro amigável a partir do Set de campos faltando.
 * Se `obj` for passado, adiciona hint específico pro telefone curto.
 */
export function buildErrorMessage(missing, obj = null) {
  if (!missing || missing.size === 0) return "";
  const labels = [...missing].map((k) => {
    // Telefone: se tem valor mas é inválido, deixa claro que é o formato
    if (k === "cliente_telefone" && obj && String(obj.cliente_telefone || "").trim()) {
      return "Telefone (mínimo 10 dígitos)";
    }
    return FIELD_LABELS[k] || k;
  });
  if (labels.length === 1) return `Preencha o campo obrigatório: ${labels[0]}`;
  return `Preencha os campos obrigatórios: ${labels.join(", ")}`;
}
